import { ref, computed, useContext, useFetch, ssrRef } from "@nuxtjs/composition-api";
import {
  FRONT_PAGE_MODULES,
  DEFAULT_MODULE_CONFIGS,
  getBaseSlug,
} from "~/config/frontPageModules";
import { useFrontPageDataStore } from "~/stores/frontPageData";

export default function useFrontPageModules() {
  const { $query, $brand, $ab, $logger, $flags } = useContext();
  const frontPageData = useFrontPageDataStore();

  const modules = ssrRef([], "frontPageModules");
  const versionId = ssrRef(null, "frontPageVersionId");
  const loading = ref(true);

  const visibleModules = computed(() =>
    modules.value
      .filter((m) => m.isVisible)
      .sort((a, b) => a.position - b.position)
  );

  const modulesByRow = computed(() => {
    const rows = {};
    for (const mod of visibleModules.value) {
      if (!rows[mod.row]) {
        rows[mod.row] = [];
      }
      rows[mod.row].push(mod);
    }
    return Object.keys(rows)
      .sort((a, b) => Number(a) - Number(b))
      .map((rowKey) => rows[rowKey]);
  });

  const getComponent = (slug) => {
    const loader = FRONT_PAGE_MODULES[slug] || FRONT_PAGE_MODULES[getBaseSlug(slug)];
    if (!loader) {
      $logger?.warn(`Unknown front page module slug: ${slug}`);
      return null;
    }
    return () => loader();
  };

  const isPriceOffer = $brand?.isPriceOffer || false;
  const brandName = $brand?.name || "joszaki";

  // Resolve brand key for config lookup
  const getBrandKey = () => {
    if (isPriceOffer) return "price-offer-creator";
    // Map brand name to config key
    const brandMap = {
      JóSzaki: "joszaki",
      SuperProfik: "superprofik-cz",
      BerufExperten: "berufexperten-de",
    };
    return brandMap[brandName] || "joszaki";
  };

  const loadFromApi = async () => {
    // Resolve PostHog experiment variant if applicable
    let posthogExperimentId = null;
    let posthogVariant = null;

    if ($flags?.isFeatureEnabled("front-page-ab-test")) {
      posthogExperimentId = "front-page-module-order";
      if ($ab?.isVariant("front-page-module-order", "test")) {
        posthogVariant = "test";
      } else {
        posthogVariant = "control";
      }
    }

    const GET_CONFIG = await import(
      "~/components/_refactored/conductor/front-page-modules/getActiveFrontPageConfig.graphql"
    );
    const result = await $query(
      GET_CONFIG.default || GET_CONFIG,
      {
        posthogExperimentId,
        posthogVariant,
      },
      {
        showErrorPage: false,
        showErrorToast: false,
      }
    );

    if (result?.activeFrontPageConfig) {
      versionId.value = result.activeFrontPageConfig.versionId;
      modules.value = result.activeFrontPageConfig.modules.map((m) => ({
        ...m,
        versionId: result.activeFrontPageConfig.versionId,
      }));
      return true;
    }
    return false;
  };

  const loadDefaults = (brandKey) => {
    modules.value = (DEFAULT_MODULE_CONFIGS[brandKey] || []).map((m) => ({
      ...m,
      versionId: null,
    }));
  };

  useFetch(async () => {
    const brandKey = getBrandKey();

    try {
      const loaded = await loadFromApi();
      if (!loaded) {
        loadDefaults(brandKey);
      }
    } catch {
      loadDefaults(brandKey);
    } finally {
      loading.value = false;
    }

    // For price-offer modules, preload shared data
    if (isPriceOffer) {
      await Promise.all([
        frontPageData.loadServiceType($query),
        frontPageData.loadFaqs($query),
      ]);
    }
  });

  return {
    modules,
    visibleModules,
    modulesByRow,
    versionId,
    loading,
    getComponent,
  };
}
